import {
	ATHENA_INGRESS_MOLLIE_ENVELOPE_INVALID,
	AthenaEventIngressError,
} from "../../../../../runtime/ingress/errors.ts";
import { MOLLIE_WEBHOOK_MAX_BODY_BYTES } from "./verify-webhook.ts";

export const MOLLIE_WEBHOOK_MAX_AGE_MS = 15 * 60 * 1000;

export type MollieWebhookEnvelope =
	| { kind: "classic"; id: string }
	| {
		kind: "next_gen";
		id: string;
		type: string;
		resource: string;
		entityId: string;
		createdAt: string;
	};

function decodeBody(body: Uint8Array): string {
	return new TextDecoder().decode(body);
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return value != null && typeof value === "object" && !Array.isArray(value);
}

function assertMollieWebhookBodySize(body: Uint8Array): void {
	if (body.byteLength > MOLLIE_WEBHOOK_MAX_BODY_BYTES) {
		throw new AthenaEventIngressError({
			code: ATHENA_INGRESS_MOLLIE_ENVELOPE_INVALID,
			domain: "billing",
			message: "Mollie webhook payload exceeds size limit.",
			provider: "mollie",
			retryable: false,
		});
	}
}

function assertMollieWebhookCreatedAt(
	createdAt: string | undefined,
): asserts createdAt is string {
	if (createdAt == null || createdAt.trim() === "") {
		throw new AthenaEventIngressError({
			code: ATHENA_INGRESS_MOLLIE_ENVELOPE_INVALID,
			domain: "billing",
			message: "Mollie webhook createdAt is required.",
			provider: "mollie",
			retryable: false,
		});
	}
	const created = Date.parse(createdAt);
	if (!Number.isFinite(created)) {
		throw new AthenaEventIngressError({
			code: ATHENA_INGRESS_MOLLIE_ENVELOPE_INVALID,
			domain: "billing",
			message: "Mollie webhook createdAt is invalid.",
			provider: "mollie",
			retryable: false,
		});
	}
	if (Math.abs(Date.now() - created) > MOLLIE_WEBHOOK_MAX_AGE_MS) {
		throw new AthenaEventIngressError({
			code: ATHENA_INGRESS_MOLLIE_ENVELOPE_INVALID,
			domain: "billing",
			message: "Mollie webhook createdAt is stale.",
			provider: "mollie",
			retryable: false,
		});
	}
}

export function parseMollieWebhookBody(body: Uint8Array): MollieWebhookEnvelope {
	assertMollieWebhookBodySize(body);
	const text = decodeBody(body).trim();
	if (text.startsWith("{")) {
		const parsed: unknown = JSON.parse(text);
		if (
			isRecord(parsed) &&
			typeof parsed.id === "string" &&
			typeof parsed.entityId === "string"
		) {
			const createdAt =
				typeof parsed.createdAt === "string" ? parsed.createdAt : undefined;
			assertMollieWebhookCreatedAt(createdAt);
			return {
				kind: "next_gen",
				id: parsed.id,
				type: typeof parsed.type === "string" ? parsed.type : "unknown",
				resource:
					typeof parsed.resource === "string" ? parsed.resource : "event",
				entityId: parsed.entityId,
				createdAt,
			};
		}
		throw new AthenaEventIngressError({
			code: ATHENA_INGRESS_MOLLIE_ENVELOPE_INVALID,
			domain: "billing",
			message: "Mollie webhook envelope is invalid.",
			provider: "mollie",
			retryable: false,
		});
	}
	const params = new URLSearchParams(text);
	const id = params.get("id");
	if (!id) {
		throw new AthenaEventIngressError({
			code: ATHENA_INGRESS_MOLLIE_ENVELOPE_INVALID,
			domain: "billing",
			message: "Mollie webhook envelope is invalid.",
			provider: "mollie",
			retryable: false,
		});
	}
	return { kind: "classic", id };
}
